import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Plus, FolderOpen, ArrowRight, Loader2, Copy, Check, Code, Key } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNewProject, setShowNewProject] = useState(false);
  const [showSDK, setShowSDK] = useState(false);
  const [sdkCode, setSdkCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchSDK();
  }, []);

  const fetchProjects = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/projects`, { withCredentials: true });
      setProjects(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Failed to load projects");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSDK = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sdk/download`, { withCredentials: true });
      setSdkCode(data.sdk);
    } catch (error) {
      console.error("Failed to fetch SDK");
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setCreating(true);

    const formData = new FormData(e.target);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/projects`,
        {
          name: formData.get("name"),
          description: formData.get("description")
        },
        { withCredentials: true }
      );
      setProjects([data, ...projects]);
      setShowNewProject(false);
      toast.success("Project created successfully");
      e.target.reset();
    } catch (error) {
      toast.error("Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'sdk') {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } else {
      setApiKeyCopied(true);
      setTimeout(() => setApiKeyCopied(false), 2000);
    }
    toast.success("Copied to clipboard");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-black tracking-tight font-heading" data-testid="dashboard-title">
            Welcome, {user?.name}
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your RAG debugging projects
          </p>
        </div>

        {/* API Key Section */}
        <Card className="mb-8 border border-border shadow-none" data-testid="api-key-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Key className="w-5 h-5 text-muted-foreground" />
                <CardTitle className="text-lg font-bold">Your API Key</CardTitle>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowSDK(true)} data-testid="view-sdk-button">
                <Code className="w-4 h-4 mr-2" />
                View SDK
              </Button>
            </div>
            <CardDescription>Use this key to send traces from your application</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-muted px-3 py-2 rounded-sm font-mono text-sm" data-testid="api-key-display">
                {user?.api_key || "Loading..."}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(user?.api_key, 'key')}
                data-testid="copy-api-key-button"
              >
                {apiKeyCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Projects Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold font-heading">Projects</h2>
          <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
            <DialogTrigger asChild>
              <Button data-testid="create-project-button">
                <Plus className="w-4 h-4 mr-2" />
                New Project
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Project</DialogTitle>
                <DialogDescription>
                  Add a new project to start collecting RAG traces
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateProject} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="project-name">Project Name</Label>
                  <Input
                    id="project-name"
                    name="name"
                    placeholder="My RAG Application"
                    required
                    data-testid="project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="project-description">Description (optional)</Label>
                  <Textarea
                    id="project-description"
                    name="description"
                    placeholder="Describe your project..."
                    data-testid="project-description-input"
                  />
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button type="button" variant="outline">Cancel</Button>
                  </DialogClose>
                  <Button type="submit" disabled={creating} data-testid="create-project-submit">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Create Project
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border border-border shadow-none animate-pulse">
                <CardHeader>
                  <div className="h-5 bg-muted rounded w-1/2" />
                  <div className="h-4 bg-muted rounded w-3/4 mt-2" />
                </CardHeader>
                <CardContent>
                  <div className="h-4 bg-muted rounded w-1/4" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <Card className="border border-border shadow-none" data-testid="empty-projects">
            <CardContent className="py-12 text-center">
              <FolderOpen className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No projects yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first project to start debugging your RAG pipelines
              </p>
              <Button onClick={() => setShowNewProject(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Project
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
            {projects.map((project) => (
              <Link key={project.id} to={`/projects/${project.id}`} data-testid={`project-card-${project.id}`}>
                <Card className="border border-border shadow-none hover:border-foreground/30 transition-colors h-full">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg font-bold">{project.name}</CardTitle>
                      <ArrowRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <CardDescription className="line-clamp-2">
                      {project.description || "No description"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Badge variant="secondary" className="font-mono">
                      {project.trace_count || 0} traces
                    </Badge>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* SDK Dialog */}
        <Dialog open={showSDK} onOpenChange={setShowSDK}>
          <DialogContent className="max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Code className="w-5 h-5" />
                RAG Debugger SDK
              </DialogTitle>
              <DialogDescription>
                Copy this SDK into your project to start sending traces
              </DialogDescription>
            </DialogHeader>
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="absolute top-2 right-2 z-10"
                onClick={() => copyToClipboard(sdkCode, 'sdk')}
                data-testid="copy-sdk-button"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
              <ScrollArea className="h-[50vh]">
                <pre className="bg-slate-950 text-slate-50 p-4 rounded-sm text-sm font-mono overflow-x-auto">
                  {sdkCode}
                </pre>
              </ScrollArea>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Close</Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
